import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import { getAdminRestaurantsList, AdminRestaurant } from '../../../services/admin/adminService';
import { SendHistoryPanel } from './communication/SendHistoryPanel';

/** The outbound email send log — kept apart from the send surfaces on the Send Reports tab. */
const AdminCommunicationEmailLogsPage = () => {
  // Super-admin tooling is always English regardless of the app's selected language.
  const { i18n } = useTranslation();
  // getFixedT returns a new function identity every call — memoize it, or effects
  // that depend on `t` re-run on every render.
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);

  // The history panel filters by restaurant, so it needs the same list the Reports tab loads.
  const [restaurants, setRestaurants] = useState<AdminRestaurant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const list = await getAdminRestaurantsList();
        setRestaurants(list);
      } catch (err) {
        console.error('Failed to load restaurants:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={secondaryText}>{t('common.loading')}</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h2 className={cn(primaryText, 'text-lg', semiBoldText)}>
            {t('adminTesting.emailLogsPage.title')}
          </h2>
          <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
            {t('adminTesting.emailLogsPage.description')}
          </p>
        </div>

        <SendHistoryPanel restaurants={restaurants} />
      </div>
    </AdminLayout>
  );
};

export default AdminCommunicationEmailLogsPage;
